import { useEffect, useRef } from "react";

export type UsageActorType = "admin" | "artist" | "client" | "anon" | "staff";

interface UseUsageTrackerOptions {
  actorType?: UsageActorType;
  actorId?: string | null;
  properties?: Record<string, unknown>;
  disabled?: boolean;
}

/**
 * Best-effort first-party page-view tracker.
 * Fires ONE event per component mount via POST /api/app-events.
 * Never throws — failure is silently swallowed so it never breaks the page.
 *
 * Usage:
 *   useUsageTracker("admin_cash_flow_viewed", { actorType: "admin" });
 */
export function useUsageTracker(
  eventName: string,
  options: UseUsageTrackerOptions = {},
) {
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current || options.disabled) return;
    fired.current = true;

    const { actorType = "admin", actorId = null, properties = {} } = options;

    fetch("/api/app-events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        events: [{ eventName, actorType, actorId, properties }],
      }),
    }).catch(() => { /* intentionally silent: best-effort side-effect */ });
  }, []);
}
